export const IMS_ACCESS_TOKEN_REFRESH_THRESHOLD_SECONDS = 60

const DEFAULT_ACCESS_TOKEN_EXPIRES_IN_HOURS = 1
const HOUR_MS = 60 * 60 * 1000
const MIN_TIMER_DELAY_MS = 1000

/**
 * @description 按 IMS access 刷新接口契约计算绝对过期时间：create_time 为 Unix 秒，expires_in 为小时。
 * @param {Object} options - 时间计算参数
 * @param {number|string|null|undefined} options.createTime - access token 创建时间戳（秒）
 * @param {number|string|null|undefined} options.expiresInHours - access token 有效时长（小时）
 * @param {number} [options.now=Date.now()] - 当前毫秒时间戳
 * @returns {number} access token 绝对过期时间（毫秒）
 */
export function resolveAccessTokenExpiresAt({ createTime, expiresInHours, now = Date.now() }) {
  const parsedCreateTime = Number(createTime)
  const createTimeMs =
    Number.isFinite(parsedCreateTime) && parsedCreateTime > 0 ? parsedCreateTime * 1000 : now
  const parsedExpiresInHours = Number(expiresInHours)
  const validHours =
    Number.isFinite(parsedExpiresInHours) && parsedExpiresInHours > 0
      ? parsedExpiresInHours
      : DEFAULT_ACCESS_TOKEN_EXPIRES_IN_HOURS

  return createTimeMs + validHours * HOUR_MS
}

/**
 * @description 计算下一次刷新定时器延时，正常按到期前 60 秒触发，也允许无推进响应延后到指定时刻。
 * @param {Object} options - 定时参数
 * @param {number} options.expiresAt - access token 绝对过期时间（毫秒）
 * @param {number} [options.now=Date.now()] - 当前毫秒时间戳
 * @param {number} [options.notBefore=0] - 本次刷新不得早于的毫秒时间戳
 * @returns {number} setTimeout 延时（毫秒）
 */
export function resolveTokenRefreshDelay({ expiresAt, now = Date.now(), notBefore = 0 }) {
  const refreshAt = Math.max(
    Number(expiresAt) - IMS_ACCESS_TOKEN_REFRESH_THRESHOLD_SECONDS * 1000,
    Number(notBefore) || 0
  )

  return Math.max(MIN_TIMER_DELAY_MS, refreshAt - now)
}

/**
 * @description 判断一次成功响应是否真正推进了会话，防止服务端返回旧 token 后立即再次刷新。
 * @param {Object} options - 刷新前后状态
 * @param {string|null|undefined} options.previousToken - 刷新前 access token
 * @param {string|null|undefined} options.nextToken - 刷新后 access token
 * @param {number|null|undefined} options.previousExpiresAt - 刷新前过期时间（毫秒）
 * @param {number} options.nextExpiresAt - 刷新后过期时间（毫秒）
 * @param {number} [options.now=Date.now()] - 当前毫秒时间戳
 * @returns {boolean} token 或过期时间已推进，且新 token 已离开刷新窗口
 */
export function hasTokenRefreshProgressed({
  previousToken,
  nextToken,
  previousExpiresAt,
  nextExpiresAt,
  now = Date.now(),
}) {
  const previousExpiry = Number(previousExpiresAt) || 0
  const nextExpiry = Number(nextExpiresAt) || 0
  const hasUsableLifetime = nextExpiry > now + IMS_ACCESS_TOKEN_REFRESH_THRESHOLD_SECONDS * 1000
  const tokenChanged = Boolean(nextToken) && nextToken !== previousToken
  const expiryAdvanced = nextExpiry > previousExpiry

  return hasUsableLifetime && (tokenChanged || expiryAdvanced)
}

/**
 * @description 服务端成功但未推进 token 时，只允许在当前 token 实际过期后进行一次补偿刷新。
 * @param {Object} options - 补偿刷新参数
 * @param {number} options.expiresAt - 当前 token 过期时间（毫秒）
 * @param {number} [options.now=Date.now()] - 当前毫秒时间戳
 * @returns {number} 补偿刷新最早执行时间（毫秒）
 */
export function resolveNoProgressRetryAt({ expiresAt, now = Date.now() }) {
  return Math.max(Number(expiresAt) + MIN_TIMER_DELAY_MS, now + MIN_TIMER_DELAY_MS)
}
